import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	useLocation,
	useNavigate,
	useParams,
	useSearch,
} from "@tanstack/react-router";
import {
	Calendar,
	CalendarCheck01,
	Hash01,
	Maximize02,
	Trash01,
} from "@untitledui/icons";
import { useMemo, useState } from "react";
import { DeleteAssetModal } from "@/components/Organisms/DeleteAssetModal/DeleteAssetModal";
import { DestinyEntityDrawer } from "@/components/Organisms/DestinyEntityDrawer/DestinyEntityDrawer";
import {
	DetailsState,
	DetailsTab,
} from "@/components/Organisms/Details/Details";
import { DrawerProvider } from "@/contexts/drawer-context";
import {
	AssetDetailsTab,
	AssetEntityDetails,
} from "@/features/assets/components";
import { getAssetDisplayTitle } from "@/features/assets/lib/get-asset-display-title.util";
import { getAssetWorkspacePath } from "@/features/jobs/lib/get-asset-workspace-path.util";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useDestinyDrawerCurrentTab } from "@/lib/use-destiny-drawer-current-tab";
import { assetInstanceQueryOptions } from "@/services/asset";
import { authQueryOptions } from "@/services/auth";
import {
	createdMetadataLabel,
	updatedMetadataLabel,
} from "@/utils/entity-metadata-date-label";

export type AssetDrawerTab = "details" | "activity";

export const Route = createFileRoute(
	"/_authenticated/(web-app)/_web/web/$orgId/jobs/(job)/$jobId/workspace/assets/$assetId/_drawer",
)({
	component: RouteComponent,
});

function RouteComponent() {
	return (
		<DrawerProvider>
			<AssetDrawer />
		</DrawerProvider>
	);
}

/**
 * Job workspace Asset detail drawer — DestinyEntityDrawer + AssetEntityDetails.
 */
function AssetDrawer() {
	const navigate = useNavigate();
	const location = useLocation();
	const { t } = useAppTranslation();
	const params = useParams({
		from: "/_authenticated/(web-app)/_web/web/$orgId/jobs/(job)/$jobId/workspace/assets/$assetId/_drawer",
	});
	const search = useSearch({
		from: "/_authenticated/(web-app)/_web/web/$orgId/jobs/(job)/$jobId/workspace/assets",
		shouldThrow: false,
	});

	const [isOpen, setIsOpen] = useState(true);
	const [isDeleteOpen, setIsDeleteOpen] = useState(false);

	const currentTab = useDestinyDrawerCurrentTab(location.pathname);

	const assetQuery = useQuery({
		...assetInstanceQueryOptions.detail(params.assetId, {
			organizationId: params.orgId,
		}),
		retry: false,
	});

	const canDeleteAsset = useQuery({
		...authQueryOptions.checkAction({
			name: "DeleteAsset",
			entityType: "Asset",
			entityId: params.assetId,
		}),
		enabled: !!params.assetId,
	});

	const drawerMetadata = useMemo(() => {
		const asset = assetQuery.data;
		if (!asset) return [];

		const items: {
			id: string;
			label: string;
			icon: typeof Hash01;
		}[] = [];

		if (asset.assetNumber != null) {
			items.push({
				id: "asset-number",
				label: String(asset.assetNumber),
				icon: Hash01,
			});
		}
		if (asset.createdAt) {
			items.push({
				id: "created-at",
				label: createdMetadataLabel(t, asset.createdAt),
				icon: Calendar,
			});
		}
		if (asset.updatedAt) {
			items.push({
				id: "updated-at",
				label: updatedMetadataLabel(t, asset.updatedAt),
				icon: CalendarCheck01,
			});
		}
		return items;
	}, [assetQuery.data, t]);

	const searchQ = typeof search?.q === "string" ? search.q : undefined;

	const handleOpenWorkspace = () => {
		void navigate({
			href: getAssetWorkspacePath({
				orgId: params.orgId,
				jobId: params.jobId,
				assetId: params.assetId,
			}),
		});
	};

	const handleClose = () => {
		setIsOpen(false);
		setTimeout(() => {
			void navigate({
				to: "/web/$orgId/jobs/$jobId/workspace/assets",
				params: { orgId: params.orgId, jobId: params.jobId },
				search: { q: searchQ },
				replace: true,
			});
		}, 200);
	};

	const handleOpenChange = (open: boolean) => {
		setIsOpen(open);
		if (!open) {
			handleClose();
		}
	};

	const title = getAssetDisplayTitle({
		name: assetQuery.data?.name,
		assetNumber: assetQuery.data?.assetNumber,
	});

	const tabs = [
		{
			label: t("main.jobs.workspace.assets.drawer.tabs.details.label"),
			id: AssetDetailsTab.Details,
		},
		{
			label: t("main.jobs.workspace.assets.drawer.tabs.activity.label"),
			id: AssetDetailsTab.Activity,
		},
	];

	const handleTabChange = (tabId: string) => {
		if (tabId === currentTab) return;
		const to =
			tabId === DetailsTab.Activity
				? "/web/$orgId/jobs/$jobId/workspace/assets/$assetId/activity"
				: "/web/$orgId/jobs/$jobId/workspace/assets/$assetId/details";
		void navigate({
			to,
			params: {
				orgId: params.orgId,
				jobId: params.jobId,
				assetId: params.assetId,
			},
			search: { q: searchQ },
		});
	};

	return (
		<DestinyEntityDrawer
			type="asset"
			isOpen={isOpen}
			onOpenChange={handleOpenChange}
			title={title}
			metadata={drawerMetadata}
			isError={assetQuery.isError}
			errorDescription={t("main.jobs.workspace.assets.drawer.loadFailed")}
			tabs={tabs}
			currentTab={currentTab}
			onTabChange={handleTabChange}
			data-testid="job-workspace-asset-drawer"
			actions={[
				{
					label: t("main.jobs.workspace.action"),
					icon: Maximize02,
					color: "secondary",
					onPress: handleOpenWorkspace,
				},
			]}
			overflowActions={
				canDeleteAsset.data !== false
					? [
							{
								label: t("main.jobs.workspace.assets.drawer.delete.action"),
								icon: Trash01,
								onPress: () => setIsDeleteOpen(true),
							},
						]
					: []
			}
			details={
				<AssetEntityDetails
					orgId={params.orgId}
					assetId={params.assetId}
					state={
						assetQuery.isPending ? DetailsState.Loading : DetailsState.Default
					}
					selectedTabId={currentTab}
					onTabChange={handleTabChange}
					tabs={tabs}
					className="max-md:[&_[role=tablist]]:hidden"
				/>
			}
			outlet={<Outlet />}
		>
			{() => (
				<DeleteAssetModal
					isOpen={isDeleteOpen}
					setIsOpen={setIsDeleteOpen}
					handleClose={() => setIsDeleteOpen(false)}
					onDeleteSuccess={handleClose}
					assetId={params.assetId}
					organizationId={params.orgId}
				/>
			)}
		</DestinyEntityDrawer>
	);
}
